import type { TFunction } from "i18next";
import { X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";

import { useAuditEvents } from "../api/use-audit-events";
import { AuditDetailDrawer } from "../components/AuditDetailDrawer";
import { AuditExportButton } from "../components/AuditExportButton";
import { AuditFilters } from "../components/AuditFilters";
import { AuditTable } from "../components/AuditTable";
import { parseAuditSearch, resetPage, type AuditFilterState } from "../lib/url-state";

interface AuditLogPageProps {
  urlFilter: AuditFilterState;
  onUrlFilterChange: (next: AuditFilterState) => void;
}

/**
 * Filter state is route-provided: the `/audit-log` route calls
 * `Route.useSearch()`/`Route.useNavigate()` (typed off `validateSearch`) and
 * passes the result down as `urlFilter`/`onUrlFilterChange` — same pattern as
 * OrdersPage/InventoryPage. Keeps this component free of router hooks (and
 * the manual casts they'd otherwise require), and easy to mount under any
 * harness that supplies those two props.
 */
export function AuditLogPage({ urlFilter, onUrlFilterChange }: AuditLogPageProps): JSX.Element {
  const { t } = useTranslation(["audit", "nav"]);
  const filter = urlFilter;

  const { data, isLoading, isError, refetch } = useAuditEvents(filter);
  const events = data?.items ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 50;

  // `occurrences`/`first_seen`/`last_seen` exist only on a grouped list row —
  // GET /v1/audit-events/{id} returns one event and can't know its group — so
  // the open row's group info travels from the list into the drawer.
  const selected = events.find((e) => e.id === filter.event_id);

  function handleFilterChange(patch: Partial<AuditFilterState>): void {
    onUrlFilterChange(resetPage({ ...filter, ...patch }));
  }

  function handleClear(): void {
    onUrlFilterChange(parseAuditSearch({}));
  }

  function handleRowActivate(eventId: number): void {
    onUrlFilterChange({ ...filter, event_id: eventId });
  }

  // Closing removes ONLY event_id — every other filter/param survives untouched.
  function handleDrawerClose(): void {
    onUrlFilterChange({ ...filter, event_id: undefined });
  }

  // "Related events": closes the drawer (event_id omitted), drops every
  // other filter (a correlation ID can span modules the current filters
  // would otherwise hide), replaces search with the exact correlation ID,
  // and preserves the currently-bounded date range instead of resetting to
  // the 7-day default the way handleClear() would.
  function handleRelatedEvents(correlationId: string): void {
    onUrlFilterChange(
      parseAuditSearch({
        preset: filter.preset,
        date_from: filter.date_from,
        date_to: filter.date_to,
        search: correlationId,
      }),
    );
  }

  // "Timeline for this order/SKU": the object's whole story, so every filter
  // that could hide part of it goes (module, activity, actor, result,
  // attention, search) while the chosen date window stays.
  // A timeline is an object's whole story, and an order's first events are
  // usually days older than the failure someone clicked on — so it always
  // opens on the widest preset rather than inheriting a 24h window that
  // would show a single row.
  function handleTimeline(relatedTo: string): void {
    onUrlFilterChange({
      ...parseAuditSearch({ preset: "30d" }),
      related_to: relatedTo,
    });
  }

  function handleClearTimeline(): void {
    onUrlFilterChange(resetPage({ ...filter, related_to: undefined }));
  }

  return (
    <div data-testid="audit-log-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.admin")}</span>
            <span className="text-muted-foreground">/</span>
            <span>{t("page.title")}</span>
          </>
        }
        title={t("page.title")}
        subtitle={`${t("page.subtitle")} ${t("page.coverage_note")}`}
        actions={<AuditExportButton filter={filter} />}
      />

      <div className="mb-4">
        <AuditFilters filter={filter} onChange={handleFilterChange} onClear={handleClear} />
      </div>

      {filter.related_to !== undefined && (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-[12px] border border-[var(--brand-500)] bg-[var(--surface-spotlight)] px-3 py-2 text-sm"
          data-testid="audit-timeline-banner"
        >
          <span className="font-semibold text-[var(--text-primary)]">
            {t("timeline.banner", {
              kind: timelineKind(t, filter.related_to),
              id: timelineId(filter.related_to),
              count: total,
            })}
          </span>
          <button
            type="button"
            onClick={handleClearTimeline}
            data-testid="audit-timeline-clear"
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-2.5 text-xs font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            {t("timeline.clear")}
          </button>
        </div>
      )}

      <AuditTable
        events={events}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        onRowActivate={handleRowActivate}
      />

      {!isLoading && !isError && (
        <Pagination
          total={total}
          offset={filter.offset}
          limit={limit}
          pageSizeOptions={[limit]}
          onChange={({ offset }) => onUrlFilterChange({ ...filter, offset })}
        />
      )}

      <AuditDetailDrawer
        eventId={filter.event_id}
        {...(selected !== undefined && {
          groupInfo: {
            ...(selected.occurrences !== undefined && { occurrences: selected.occurrences }),
            ...(selected.first_seen !== undefined && { first_seen: selected.first_seen }),
            ...(selected.last_seen !== undefined && { last_seen: selected.last_seen }),
          },
        })}
        onClose={handleDrawerClose}
        onRelatedEvents={handleRelatedEvents}
        onTimeline={handleTimeline}
      />
    </div>
  );
}

/** `related_to` is validated as `^(order|sku):.+$` by url-state, so the split
 * always yields both halves. */
function timelineKind(t: TFunction<["audit", "nav"]>, relatedTo: string): string {
  return relatedTo.startsWith("sku:") ? t("timeline.kind_sku") : t("timeline.kind_order");
}

function timelineId(relatedTo: string): string {
  return relatedTo.slice(relatedTo.indexOf(":") + 1);
}
